import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLink,
  faTimes,
  faSpinner,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import {
  findUnlinkedWalletsForEmail,
  addPartnerWallet,
  listPartnerWallets,
  getVolumes,
  getTransactions,
} from "../../store/actions";

const DISMISS_PREFIX = "dismissed_link_";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

const shortHash = (addr) => {
  if (!addr || typeof addr !== "string") return "";
  if (addr.length <= 12) return addr;
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
};

const normalize = (addr) => (addr || "").toLowerCase();

const isDismissed = (wallet) => {
  try {
    const key = `${DISMISS_PREFIX}${normalize(wallet)}`;
    const raw = localStorage.getItem(key);
    if (!raw) return false;
    const ts = Number(raw);
    if (!ts || Number.isNaN(ts)) {
      localStorage.removeItem(key);
      return false;
    }
    if (Date.now() - ts > DISMISS_TTL_MS) {
      localStorage.removeItem(key);
      return false;
    }
    return true;
  } catch {
    return false;
  }
};

const dismiss = (wallet) => {
  try {
    localStorage.setItem(
      `${DISMISS_PREFIX}${normalize(wallet)}`,
      String(Date.now())
    );
  } catch {
    // localStorage unavailable — silently ignore
  }
};

const sourceLabel = (src) => {
  const s = (src || "").toLowerCase();
  if (s.includes("trade")) return "trade.instaswap.com";
  if (s.includes("swap")) return "swap.instaswap.com";
  if (s.includes("website") || s.includes("instaswap.com")) return "instaswap.com";
  if (s.includes("privy")) return "instaswap.com";
  return src || "another instaswap app";
};

const UnlinkedWalletsBanner = () => {
  const dispatch = useDispatch();
  const unlinked = useSelector(
    (state) => state.userReducer.unlinkedWallets
  );

  const [linking, setLinking] = useState({}); // { [walletAddress]: true }
  const [dismissedTick, setDismissedTick] = useState(0);

  useEffect(() => {
    dispatch(findUnlinkedWalletsForEmail());
  }, [dispatch]);

  const handleLink = useCallback(
    async (wallet) => {
      setLinking((prev) => ({ ...prev, [wallet]: true }));
      try {
        await dispatch(addPartnerWallet(wallet, "banner"));
        toast.success(`Linked ${shortHash(wallet)} to your partner profile.`);
        // Refresh related views so aggregated stats reflect the new wallet
        dispatch(findUnlinkedWalletsForEmail());
        dispatch(listPartnerWallets());
        dispatch(getVolumes());
        dispatch(getTransactions());
      } catch (err) {
        if (err.status === 409 && err.conflictingPartner) {
          toast.error(
            `This wallet is already linked to partner ${err.conflictingPartner}.`
          );
        } else {
          toast.error(err.message || "Failed to link wallet");
        }
      } finally {
        setLinking((prev) => {
          const next = { ...prev };
          delete next[wallet];
          return next;
        });
      }
    },
    [dispatch]
  );

  const handleDismiss = useCallback((wallet) => {
    dismiss(wallet);
    setDismissedTick((t) => t + 1);
  }, []);

  const visible = (Array.isArray(unlinked) ? unlinked : []).filter(
    (entry) => entry && entry.wallet && !isDismissed(entry.wallet)
  );

  // dismissedTick is read here to keep the dependency for re-renders
  // when the user clicks Dismiss.
  void dismissedTick;

  if (visible.length === 0) return null;

  return (
    <div style={{ marginBottom: 18 }}>
      {visible.map((entry) => {
        const wallet = entry.wallet;
        const src = sourceLabel(entry.source);
        const isLinking = !!linking[wallet];
        return (
          <div
            key={wallet}
            className="notice"
            style={{
              marginBottom: 10,
              alignItems: "center",
              flexWrap: "wrap",
              gap: 14,
            }}
          >
            <FontAwesomeIcon
              icon={faTriangleExclamation}
              className="ni"
              style={{ fontSize: 18 }}
            />
            <div
              style={{ flex: "1 1 320px", fontSize: 13.5, lineHeight: 1.55 }}
            >
              We detected you also use{" "}
              <span
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  color: "#3ad17e",
                }}
                title={wallet}
              >
                {shortHash(wallet)}
              </span>{" "}
              on {src} (signed in with the same email). Link this wallet to
              your partner profile so referrals, points and swaps are unified.{" "}
              <Link
                to="/linked-wallets"
                style={{ color: "#3ad17e", textDecoration: "underline" }}
              >
                Manage linked wallets →
              </Link>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                onClick={() => handleLink(wallet)}
                disabled={isLinking}
                className="btnPrimary sm"
              >
                {isLinking ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  <>
                    <FontAwesomeIcon icon={faLink} /> Link wallet
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => handleDismiss(wallet)}
                disabled={isLinking}
                className="btnGhost sm"
                style={{
                  cursor: isLinking ? "not-allowed" : "pointer",
                  fontSize: 13,
                }}
                title="Hide for 7 days"
              >
                <FontAwesomeIcon icon={faTimes} /> Dismiss for 7 days
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default UnlinkedWalletsBanner;
